// ComposerCore — the shared conversational composer primitives
// (MR B, Owner 19-10: "DO NOT create a second unrelated composer").
//
// Owns: textarea + auto-resize, Enter/Shift+Enter keyboard behavior,
// clipboard image paste, optional drag/drop, file picker + upload
// pipeline (canonical /api/files), pending-attachment chips (thumbnail,
// name, size, remove, stable order), send gating (text OR attachment-
// only), and the send/stop button primitives.
//
// Wrappers provide their own identity around it:
//   * SearchBar (chat)  — model selector slot, no drop zone (behavior
//     identical to the pre-extraction composer; canonical paste/
//     attachment-only-send fixes preserved).
//   * ImageComposer     — semantic mode chip slot, image-only picker,
//     drop zone enabled.
//
// Uploads go through the canonical library upload (uploadFile) — file
// ids are RealityAI-owned; no provider paths ever appear here.
import { useEffect, useRef, useState } from "react";
import { ArrowUp, Plus, Square, X, FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tip } from "@/components/Tip";
import { uploadFile, type AttachmentPayload } from "@/lib/api";

export interface PendingAttachment {
  name: string;
  size: number;
  mime: string;
  kind: string;
  file_id: string;
  data_url?: string;
  text?: string;
}

export interface ComposerCoreProps {
  onSend: (query: string, attachments?: AttachmentPayload[]) => void;
  placeholder: string;
  isLoading?: boolean;
  onStop?: () => void;
  // Library attachment: files uploaded via "+" go to the user library
  // (50MB quota). Chat wrapper keeps the library-refresh callback.
  onLibraryChanged?: () => void;
  // Image-only pickers (e.g. the image workspace) restrict the dialog.
  accept?: string;
  // Drag/drop onto the composer (opt-in; chat keeps it off so its
  // behavior is byte-for-byte the canonical one).
  enableDrop?: boolean;
  // Cap on pending attachments (backend capability is the authority;
  // the wrapper passes the value from /capability, never a hard-coded
  // provider max).
  maxAttachments?: number;
  // Slot rendered in the bottom bar left side (chat: model selector;
  // image: mode chip).
  leftSlot?: React.ReactNode;
  // Slot rendered above the input box (e.g. the selected-mode chip).
  topSlot?: React.ReactNode;
  // Pre-seeded pending attachments (e.g. "Продолжить редактирование":
  // a previous result file placed back into the composer context).
  // Generic — no image-specific assumptions; the wrapper owns the data.
  initialAttachments?: PendingAttachment[];
  // Expose the current text (e.g. for a wrapper that pre-fills a
  // continue-editing prompt).
  value?: string;
  onValueChange?: (v: string) => void;
}

export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function toPayload(p: PendingAttachment): AttachmentPayload {
  return {
    kind: p.kind,
    name: p.name,
    mime: p.mime,
    size: p.size,
    file_id: p.file_id,
    ...(p.data_url ? { data_url: p.data_url } : {}),
    ...(p.text ? { text: p.text } : {}),
  };
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function ComposerCore({
  onSend,
  placeholder,
  isLoading,
  onStop,
  onLibraryChanged,
  accept,
  enableDrop,
  maxAttachments,
  leftSlot,
  topSlot,
  initialAttachments,
  value: controlledValue,
  onValueChange,
}: ComposerCoreProps) {
  const [innerQuery, setInnerQuery] = useState("");
  const query = controlledValue !== undefined ? controlledValue : innerQuery;
  const setQuery = (v: string) => {
    if (onValueChange) onValueChange(v);
    if (controlledValue === undefined) setInnerQuery(v);
  };
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [pending, setPending] = useState<PendingAttachment[]>(initialAttachments ?? []);
  // Wrapper-driven attachment context (continue-editing): when the
  // wrapper seeds a new attachment set, adopt it.
  const seededKey = initialAttachments
    ? initialAttachments.map((a) => a.file_id).join(",")
    : "";
  const lastSeededRef = useRef(seededKey);
  useEffect(() => {
    if (initialAttachments && seededKey !== lastSeededRef.current) {
      lastSeededRef.current = seededKey;
      setPending(initialAttachments);
    }
  }, [seededKey, initialAttachments]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const adjustHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  };

  useEffect(() => {
    adjustHeight();
  }, [query]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (canSend) {
        send();
      }
    }
  };

  // Send is allowed with an empty text box as long as an attachment is
  // attached — an image/screenshot alone is a valid prompt.
  const canSend = (query.trim() || pending.length > 0) && !isLoading && !uploading;

  const send = () => {
    if (!canSend) return;
    const atts = pending.map(toPayload);
    onSend(query, atts.length ? atts : undefined);
    setQuery("");
    setPending([]);
  };

  // Paste a screenshot (Ctrl+V / Cmd+V) straight into the composer: image
  // items from the clipboard go through the same upload pipeline as "+".
  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const images: File[] = [];
    for (const item of items) {
      if (item.kind === "file" && item.type.startsWith("image/")) {
        const f = item.getAsFile();
        if (f) images.push(f);
      }
    }
    if (images.length === 0) return; // plain text pastes normally
    e.preventDefault();
    void handleFiles(images);
  };

  const handleFiles = async (files: File[]) => {
    if (files.length === 0 || uploading) return;
    setUploading(true);
    try {
      for (const file of files) {
        const up = await uploadFile(file);
        const kind = up.kind;
        const item: PendingAttachment = {
          name: up.name,
          size: up.size,
          mime: up.mime || file.type,
          kind,
          file_id: up.id,
        };
        // Inline content only for what the consumer can actually use:
        // images (data_url) and text (text). PDF/binary stay in the library.
        if (kind === "image" && up.size <= 4 * 1024 * 1024) {
          item.data_url = await fileToDataUrl(file);
        } else if (kind === "text" && up.size <= 1024 * 1024) {
          item.text = await file.text();
        }
        setPending((prev) => (prev.some((p) => p.file_id === item.file_id) ? prev : [...prev, item]));
      }
      onLibraryChanged?.();
    } catch {
      // upload errors surface in the library panel / network; keep the UI calm
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeAttachment = (fileId: string) =>
    setPending((prev) => prev.filter((x) => x.file_id !== fileId));

  return (
    <div
      className="w-full max-w-3xl mx-auto relative"
      data-testid="composer-core"
      onDragOver={
        enableDrop
          ? (e) => {
              e.preventDefault();
              setDragOver(true);
            }
          : undefined
      }
      onDragLeave={enableDrop ? () => setDragOver(false) : undefined}
      onDrop={
        enableDrop
          ? (e) => {
              e.preventDefault();
              setDragOver(false);
              const files = Array.from(e.dataTransfer?.files ?? []).filter((f) =>
                f.type.startsWith("image/")
              );
              if (files.length) void handleFiles(files);
            }
          : undefined
      }
    >
      {topSlot}

      {/* Pending attachments (chips above the input) */}
      {pending.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2 px-1" data-testid="composer-attachments">
          {pending.map((p) => (
            <div
              key={p.file_id}
              className="flex items-center gap-1.5 pl-2 pr-1 h-8 rounded-lg bg-elevated border border-strongline text-xs text-primary"
            >
              {p.kind === "image" && p.data_url ? (
                <img src={p.data_url} alt="" className="w-5 h-5 rounded object-cover" />
              ) : (
                <FileText size={13} className="text-accent shrink-0" />
              )}
              <span className="max-w-[160px] truncate">{p.name}</span>
              <span className="text-tertiary shrink-0">{formatSize(p.size)}</span>
              <button
                onClick={() => removeAttachment(p.file_id)}
                className="p-0.5 rounded text-tertiary hover:text-primary transition-colors"
                aria-label="Убрать файл"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Brand highlight: composer border+glow in the brand color (same as the
          send button) when focused. */}
      <div
        className={cn(
          "relative bg-surface border rounded-[20px] shadow-lg transition-all duration-200 p-3 focus-within:border-accent focus-within:ring-1 focus-within:ring-accent/50 focus-within:shadow-[0_0_28px_rgba(34,211,238,0.16)]",
          dragOver ? "border-accent border-dashed" : "border-strongline"
        )}
      >
        <textarea
          ref={textareaRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={placeholder}
          className="w-full bg-transparent text-primary placeholder:text-tertiary px-2 py-1.5 md:py-2 resize-none focus:outline-none min-h-[40px] md:min-h-[50px] max-h-[200px] text-base"
          rows={1}
        />

        <div className="flex items-center justify-between px-2 pb-2">
          {/* Left: attach "+" + wrapper slot */}
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={accept}
              className="hidden"
              onChange={(e) => handleFiles(Array.from(e.target.files ?? []))}
            />
            <Tip label="Прикрепить файл" side="top">
              <button
                data-testid="composer-attach"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-secondary hover:text-primary hover:bg-hoverbg transition-colors disabled:opacity-50"
              >
                {uploading ? (
                  <span className="w-3.5 h-3.5 rounded-full border-2 border-accent border-t-transparent animate-spin" />
                ) : (
                  <Plus size={18} />
                )}
              </button>
            </Tip>
            {leftSlot}
          </div>

          {/* Right: stop (during generation) / send */}
          <div className="flex items-center gap-2">
            {isLoading ? (
              <Tip label="Остановить" side="top">
                <button
                  onClick={onStop}
                  className="w-10 h-10 rounded-full flex items-center justify-center bg-hoverbg2 text-primary hover:bg-hoverbg transition-all"
                >
                  <Square size={16} fill="currentColor" />
                </button>
              </Tip>
            ) : (
              <Tip label="Отправить" side="top">
                <button
                  onClick={send}
                  disabled={!canSend}
                  className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center transition-all",
                    canSend
                      ? "bg-accent text-accent-contrast hover:opacity-90 shadow-[0_0_16px_rgba(34,211,238,0.35)]"
                      : "bg-hoverbg text-disabled cursor-not-allowed"
                  )}
                >
                  <ArrowUp size={18} />
                </button>
              </Tip>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
